import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponse, GridColumn } from '../models/grid.model';
import { environment } from '../../../../environments/environment';

/**
 * Calls the liessin-api grid and export endpoints.
 * Grid:   GET /api/{path}/grid?page=0&size=10&sort=id|desc&filters=...
 * Export: GET /api/{path}/export?filters=...
 */
@Injectable({ providedIn: 'root' })
export class GridService {
  constructor(private http: HttpClient) {}

  grid<T>(
    path: string,
    page: number,
    size: number,
    sort: string[] = ['id|desc'],
    filters?: string
  ): Observable<PaginatedResponse<T>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    sort.forEach(s => { params = params.append('sort', s); });

    if (filters != null && filters !== '') {
      params = params.set('filters', filters);
    }

    return this.http.get<PaginatedResponse<T>>(
      `${environment.baseUrl}/${path}/grid`,
      { params }
    );
  }

  /**
   * Downloads an export file (CSV or XLSX).
   * Sends auth headers via the existing HTTP interceptor.
   * Backend params: type, filters, columns (JSON array of {name, alias, format}).
   */
  export(
    path: string,
    type: 'csv' | 'xlsx',
    columns: GridColumn[],
    filters?: string
  ): void {
    const exportColumns = columns.map(c => ({
      name: c.valuePath ?? c.field,
      alias: c.header,
      format: c.format ?? ''
    }));

    let params = new HttpParams()
      .set('type', type)
      .set('columns', JSON.stringify(exportColumns));

    if (filters) {
      params = params.set('filters', filters);
    }

    this.http
      .get(`${environment.baseUrl}/${path}/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .subscribe(response => {
        const blob = response.body!;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export.${type}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }
}
